<template>
  <settings-chrome
    kicker="Settings"
    title="Rostering"
    sub="How far ahead the Sunday job fills people’s hours."
    form-id="roster-settings-form"
    :dirty="dirty"
    :saving="isSaving"
    @discard="revert"
    @allow-leave="dirty = false"
  >
    <ValidationObserver v-slot="{ handleSubmit }">
      <form
        id="roster-settings-form"
        class="st-body"
        @submit.prevent="handleSubmit(submit)"
      >
        <section class="st-card">
          <div class="st-card-head">
            <div>
              <h2>Keep the roster filled</h2>
              <p class="st-lead">
                Every Sunday morning the system copies each person’s
                <strong>usual hours</strong> onto empty days, far enough ahead
                to cover this many days from that Sunday.
              </p>
            </div>
          </div>
          <label class="st-field st-field-narrow">
            Days ahead
            <ValidationProvider rules="required|min_value:0" v-slot="{ errors }">
              <fg-input
                class="mb-0"
                type="number"
                min="0"
                step="1"
                v-model.number="model.rosterRollingPeriodDays"
                :error="errors[0]"
              />
            </ValidationProvider>
          </label>
          <p v-if="rollingDays === 0" class="st-hint" style="margin-top: 14px">
            0 means the Sunday job does nothing. You add hours yourself.
          </p>
          <div v-else class="st-example">
            <p class="st-example-kicker">Example</p>
            <p>
              This Sunday is <strong>{{ sundayLabel }}</strong>. With
              <strong>{{ rollingDays }}</strong>
              {{ rollingDays === 1 ? "day" : "days" }}, the system will fill
              empty days through <strong>{{ endLabel }}</strong>.
            </p>
            <p>
              If Mary’s usual hours are Tuesday 9:00–17:00, every empty Tuesday
              in that stretch gets those hours. Days you already rostered, or
              marked sick or away, are left alone.
            </p>
          </div>
        </section>
      </form>
    </ValidationObserver>
  </settings-chrome>
</template>
<script>
import _ from "lodash";
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import { mergeShop } from "@/helpers/shopDefaults";
import SettingsChrome from "@/components/settings/SettingsChrome";

function nextSundayMorning() {
  const now = moment();
  if (now.day() === 0 && now.hour() < 5) {
    return now.clone().startOf("day");
  }
  if (now.day() === 0) {
    return now.clone().add(7, "days").startOf("day");
  }
  return now.clone().day(7).startOf("day");
}

export default {
  components: { SettingsChrome },
  data() {
    return {
      isSaving: false,
      dirty: false,
      hydrating: false,
      model: mergeShop({})
    };
  },
  computed: {
    ...mapGetters({
      shop: "shops/shop"
    }),
    rollingDays() {
      const n = Number(this.model && this.model.rosterRollingPeriodDays);
      if (!Number.isFinite(n) || n <= 0) return 0;
      return Math.floor(n);
    },
    nextSunday() {
      return nextSundayMorning();
    },
    sundayLabel() {
      return this.nextSunday.format("dddd D MMMM");
    },
    endLabel() {
      return this.nextSunday
        .clone()
        .add(this.rollingDays, "days")
        .format("dddd D MMMM");
    }
  },
  watch: {
    shop: {
      immediate: true,
      deep: true,
      handler() {
        if (this.dirty || this.isSaving) return;
        this.resetModel();
      }
    },
    model: {
      deep: true,
      handler() {
        if (this.hydrating) return;
        this.dirty = true;
      }
    }
  },
  methods: {
    ...mapActions({
      updateShop: "shops/updateShop"
    }),
    resetModel() {
      this.hydrating = true;
      this.model = mergeShop(_.cloneDeep(this.shop || {}));
      this.dirty = false;
      this.$nextTick(() => {
        this.hydrating = false;
      });
    },
    revert() {
      this.dirty = false;
      this.resetModel();
    },
    async submit() {
      this.isSaving = true;
      try {
        await this.updateShop(this.model);
        this.dirty = false;
        this.resetModel();
        this.$notify({
          type: "success",
          title: this.$t("message.success"),
          text: this.$t("message.saveSettingsSuccess")
        });
      } catch (error) {
        this.$notify({
          type: "danger",
          title: this.$t("message.error"),
          text: (error && error.message) || this.$t("message.somethingWentWrong")
        });
      } finally {
        this.isSaving = false;
      }
    }
  }
};
</script>
